import { Card, IconButton, Tooltip } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { DataGrid } from "devextreme-react/ui/data-grid.js";
import { getDateFormat } from "../../../../utils/date-format";
import Loader from "../../../../components/Loader.js";
import { getCasesReport } from "../../../../api/index.js";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomFieldsQuery,
} from "@/Features/CustomFields";
import { getCustomAttributeCellValue } from "@/Features/CustomFields/custom-fields.utils";

const CUSTOM_COLUMN_PREFIX = "custom_attribute_";

const dateFormat = () => ({
  type: getDateFormat({ isMoment: false, includeTime: false }),
});

// Same shape as the Evidence analytics grid's custom columns, hidden unless
// the field id is in visibleIds.
export const buildCustomColumn = (field, visibleIds = []) => {
  const isDate = field.editor_type === "dateBox";
  return {
    dataField: `${CUSTOM_COLUMN_PREFIX}${field.field_id}`,
    caption: field.field_name,
    visible: visibleIds.includes(field.field_id),
    allowFiltering: true,
    dataType: isDate ? "date" : undefined,
    format: isDate ? dateFormat() : undefined,
    calculateCellValue: (rowData) =>
      getCustomAttributeCellValue(rowData, field),
  };
};

const CaseReportGrid = ({
  reportInfo,
  visibleCustomFieldIds,
  onVisibleCustomFieldsChange,
}) => {
  const caseReportGrid = useRef(null);
  const queryClient = useQueryClient();

  return (
    <Card
      style={{
        padding: "20px",
        marginBottom: "20px",
        minHeight: "450px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            fontSize: "larger",
          }}
        >
          Cases
        </div>
        <div style={{ marginLeft: "auto" }}>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              marginRight: 10,
            }}
          >
            <Tooltip arrow title="Export Grid">
              <IconButton
                color="primary"
                size="small"
                disableRipple={false}
                onClick={(e) => {
                  caseReportGrid.current.instance.exportToExcel(false);
                }}
              >
                <FileDownloadOutlinedIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            <Tooltip arrow title="Refresh">
              <IconButton
                color="primary"
                size="small"
                disableRipple={false}
                onClick={(e) => {
                  queryClient.refetchQueries({
                    queryKey: ["reports:case-overview", reportInfo],
                    active: true,
                    exact: true,
                  });
                }}
              >
                <SyncOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip arrow title="Column Select">
              <IconButton
                color="primary"
                size="small"
                disableRipple={false}
                onClick={(e) => {
                  caseReportGrid.current.instance.showColumnChooser();
                }}
              >
                <ViewColumnOutlinedIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
      <Table
        caseReportGrid={caseReportGrid}
        reportInfo={reportInfo}
        visibleCustomFieldIds={visibleCustomFieldIds}
        onVisibleCustomFieldsChange={onVisibleCustomFieldsChange}
      />
    </Card>
  );
};

// Module-level so the reference is stable: devextreme-react re-applies object
// props that change identity, and re-applying columnChooser hides an open chooser.
const COLUMN_CHOOSER = {
  enabled: true,
  mode: "select",
  title: "Filter Columns",
  height: "700",
  width: "325",
};

const Table = ({
  caseReportGrid,
  reportInfo,
  visibleCustomFieldIds,
  onVisibleCustomFieldsChange,
}) => {
  const { isLoading, data: cases } = useQuery({
    queryKey: ["reports:case-overview", reportInfo],
    queryFn: () => getCasesReport(reportInfo.report_parameters || {}),
    refetchOnWindowFocus: false,
  });
  const { data: customFieldsRes, isLoading: customFieldsLoading } =
    CustomFieldsQuery.useGetCustomFields({
      category: CUSTOM_FIELD_CATEGORIES.CASE,
    });
  // Seeds custom-column visibility on mount only; after that the grid owns it.
  const [initialVisibleIds] = useState(visibleCustomFieldIds);

  // Memoized so a parent re-render does not reset the grid's column state.
  const columns = useMemo(
    () => [
      {
        dataField: "case_number",
        caption: "Case Number",
      },
      {
        dataField: "case_ref",
        caption: "Case Name",
      },
      {
        dataField: "name",
        caption: "Client Name",
      },
      {
        dataField: "organization",
        caption: "Organization",
      },
      {
        dataField: "case_open_date",
        caption: "Open Date",
        dataType: "date",
        format: dateFormat(),
      },
      {
        dataField: "case_closed_date",
        caption: "Close Date",
        dataType: "date",
        format: dateFormat(),
      },
      {
        dataField: "last_activity_date",
        caption: "Last Activity",
        dataType: "date",
        format: dateFormat(),
      },
      {
        dataField: "full_name",
        caption: "Case Lead",
      },
      {
        dataField: "case_status",
        caption: "Status",
      },
      {
        dataField: "case_type",
        caption: "Type",
      },
      {
        dataField: "progress",
      },
      ...(customFieldsRes?.data ?? []).map((field) =>
        buildCustomColumn(field, initialVisibleIds)
      ),
    ],
    [customFieldsRes, initialVisibleIds]
  );

  if (isLoading || customFieldsLoading)
    return (
      <div style={{ marginTop: 30 }}>
        <Loader message="Retrieving Data..." />
      </div>
    );

  return (
    <DataGrid
      ref={caseReportGrid}
      dataSource={cases}
      height="350px"
      columns={columns}
      onOptionChanged={(e) => {
        if (!/^columns\[\d+\]\.visible$/.test(e.fullName)) return;
        onVisibleCustomFieldsChange(
          e.component
            .getVisibleColumns()
            .map((column) => column.dataField)
            .filter((dataField) => dataField?.startsWith(CUSTOM_COLUMN_PREFIX))
            .map((dataField) =>
              Number(dataField.slice(CUSTOM_COLUMN_PREFIX.length))
            )
        );
      }}
      showRowLines={true}
      allowColumnReordering={false}
      allowColumnResizing={true}
      grouping={{ contextMenuEnabled: true }}
      onToolbarPreparing={(e) => {
        e.toolbarOptions.visible = false;
      }}
      paging={{
        pageSize: 20,
      }}
      scrolling={{
        mode: "virtual",
        rowRenderingMode: "virtual",
        useNative: false,
      }}
      headerFilter={{
        visible: true,
        allowSearch: true,
      }}
      hoverStateEnabled={true}
      columnChooser={COLUMN_CHOOSER}
      loadPanel={{
        enabled: false,
      }}
    />
  );
};

export default CaseReportGrid;
